import {
  requestAssistantRun,
  sendAssistantThreadMessage,
  deleteExistingThread,
  createRun,
  checkRunStatus,
  listMessages,
  submitToolOutputsAction,
  sendAiHelpNotif,
} from "@/services/openai-integration";

const state = {
  messages: JSON.parse(localStorage.getItem("chat_messages")) || [], // Load from localStorage
  threadId: localStorage.getItem("chat_threadId") || "", // Load from localStorage
  isLoading: false,
  error: null,
};

const getters = {
  allMessages: (state) => state.messages,
  loading: (state) => state.isLoading,
  error: (state) => state.error,
  threadId: (state) => state.threadId,
};

const mutations = {
  ADD_MESSAGE(state, message) {
    state.messages.push(message);
    // Save to localStorage
    localStorage.setItem("chat_messages", JSON.stringify(state.messages));
  },
  SET_MESSAGES(state, messages) {
    state.messages = messages;
    localStorage.setItem("chat_messages", JSON.stringify(messages));
  },
  SET_THREAD_ID(state, threadId) {
    state.threadId = threadId;
    localStorage.setItem("chat_threadId", threadId);
  },
  SET_LOADING(state, status) {
    state.isLoading = status;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_MESSAGES(state) {
    state.messages = [];
    state.threadId = "";
    // Remove from localStorage
    localStorage.removeItem("chat_messages");
    localStorage.removeItem("chat_threadId");
  },
};

const actions = {
  async sendMessage({ commit, state }, userMessage) {
    if (!userMessage.trim()) return;
    // Add the user's message immediately to the conversation.
    commit("ADD_MESSAGE", { role: "user", content: userMessage });
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);

    try {
      let response;
      let runId, threadId;
      let updatedMessages = [];
      let toolOutputSubmissionRequired = false;
      let functionArgs = {};
      const startTime = Date.now(); // Record start time
      const timeoutDuration = 5 * 60 * 1000; // 5 minutes in milliseconds

      // Check if this is a new conversation or an existing thread.
      if (!state.threadId) {
        // New conversation: send all messages and create a new thread and run
        response = await requestAssistantRun(state.messages);

        if (response.error || !response.run_id || !response.thread_id) {
          console.log(response.error);
          throw new Error(
            "Une erreur s'est produite lors du traitement de votre message. Veuillez réessayer."
          );
        }
        runId = response.run_id;
        threadId = response.thread_id;
        commit("SET_THREAD_ID", threadId); // Set threadId in Vuex state
      } else {
        // Existing thread: send the new message using the dedicated endpoint.
        response = await sendAssistantThreadMessage(state.threadId, {
          role: "user",
          content: userMessage,
        });

        // Create a new run on the existing thread
        const runResponse = await createRun(state.threadId);
        if (runResponse.error || !runResponse.id) {
          console.log(runResponse.error);
          throw new Error(
            "Une erreur s'est produite lors du traitement de votre message. Veuillez réessayer."
          );
        }
        runId = runResponse.id;
        threadId = state.threadId;

        if (!runId || !threadId) {
          throw new Error(
            "Une erreur s'est produite lors du traitement de votre message. Veuillez réessayer."
          );
        }
      }

      // Polling for run status
      let runStatusResponse;
      do {
        runStatusResponse = await checkRunStatus(threadId, runId);
        if (runStatusResponse.error) {
          console.log(runStatusResponse.error);
          throw new Error(
            "Une erreur s'est produite lors du traitement de votre message. Veuillez réessayer."
          );
        }

        if (
          runStatusResponse.status === "requires_action" &&
          runStatusResponse.required_action && // Corrected property name
          runStatusResponse.required_action.type === "submit_tool_outputs" // Corrected property name
        ) {
          toolOutputSubmissionRequired = true; // Set the flag
          const tool_outputs = [];
          for (const toolCall of runStatusResponse.required_action
            .submit_tool_outputs.tool_calls) {
            if (toolCall.function.name === "request_phone_number") {
              tool_outputs.push({
                tool_call_id: toolCall.id,
                output: "User gave phone number.",
              });
              console.log("function called");
              console.log(tool_outputs);
              functionArgs = JSON.parse(toolCall.function.arguments);
              console.log("functionArgs");
              console.log(functionArgs);
              await sendAiHelpNotif(functionArgs.phone_number);
            } else {
              // Handle other tools if necessary, for now, log them
              console.warn(
                `Tool function "${toolCall.function.name}" is required but not handled.`
              );
              // You might want to add a default or error output for unhandled tools
              tool_outputs.push({
                tool_call_id: toolCall.id,
                output: "Tool function not handled by the client.",
              });
            }
          }

          // Submit tool outputs and wait for a short period before re-polling
          console.log("Submitting tool outputs:", tool_outputs);
          await submitToolOutputsAction(threadId, runId, tool_outputs);
          await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second after submission
        } else if (runStatusResponse.status === "completed") {
          toolOutputSubmissionRequired = false; // Reset the flag when completed
          break; // Exit polling loop on completion
        } else if (runStatusResponse.status === "failed") {
          toolOutputSubmissionRequired = false; // Reset the flag when failed
          throw new Error(
            `Run failed with status: ${runStatusResponse.status}`
          );
        }

        // Check for timeout
        if (Date.now() - startTime > timeoutDuration) {
          toolOutputSubmissionRequired = false; // Reset the flag
          throw new Error(
            "Une erreur s'est produite lors du traitement de votre message. Veuillez réessayer."
          );
        }

        await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second between polls
      } while (
        toolOutputSubmissionRequired ||
        runStatusResponse.status !== "completed"
      );

      // Retrieve messages after run is completed
      const messagesResponse = await listMessages(threadId);
      if (messagesResponse.error || !messagesResponse.data) {
        console.log(messagesResponse.error);
        throw new Error(
          "Une erreur s'est produite lors de la réception des messages. Veuillez cliquer sur le bouton « Effacer la discussion » et actualiser la page."
        );
      }
      // updatedMessages = response.data; // Assuming response.data is the messages array

      // The backend response is assumed to include the latest conversation messages.
      if (messagesResponse.messages) {
        updatedMessages = messagesResponse.messages;
      } else if (messagesResponse.data && messagesResponse.data.length > 0) {
        updatedMessages = messagesResponse.data.map((msg) => {
          let content = msg.content[0].text.value;
          // Remove citations (e.g., 【7:0†source】)
          content = content.replace(/【.*?】/g, "");
          // Convert markdown links to HTML
          content = content.replace(
            /\[([^\]]+)\]\(([^)]+)\)/g,
            '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
          );
          // Convert **bold** to <strong> tags
          content = content.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
          // Remove triple backticks (```)
          content = content.replace(/```/g, "");

          return {
            role: msg.role,
            content: content,
          };
        });
      } else if (Array.isArray(messagesResponse)) {
        updatedMessages = messagesResponse;
      } else {
        updatedMessages = [
          ...state.messages,
          {
            role: "assistant",
            content:
              "Une erreur s'est produite lors du traitement de votre message. Veuillez réessayer.",
          },
        ];
      }

      // Replace the entire messages list with the updated conversation.
      commit("SET_MESSAGES", updatedMessages);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.message ||
          "Une erreur s'est produite lors du traitement de votre message. Veuillez réessayer."
      );
      commit("ADD_MESSAGE", {
        role: "assistant",
        content:
          "Une erreur s'est produite lors du traitement de votre message. Veuillez réessayer.",
      });
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async clearMessages({ commit, state }) {
    try {
      // Only delete thread if one exists
      if (state.threadId) {
        await deleteExistingThread(state.threadId);
      }
      // Proceed to clear local state
      commit("CLEAR_MESSAGES");
    } catch (error) {
      console.error("Error deleting thread:", error);
      // Still clear messages even if API call fails
      commit("CLEAR_MESSAGES");
    }
  },
  // Optionally: add additional actions to refresh run status if needed.
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
